import type { Dispatch, SetStateAction } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { WordPressSiteOption } from "@/lib/composerCore";
import type { EditorState } from "../connectorManagerHelpers";

type Props = {
  editor: EditorState;
  setEditor: Dispatch<SetStateAction<EditorState>>;
  wpStatusLoading: boolean;
  wpLinked: boolean | null;
  wpSites: WordPressSiteOption[] | null;
  wpSitesLoading: boolean;
  wpSitesError: string | null;
  loadWpSites: () => void | Promise<void>;
};

export const WordPressFields = ({
  editor,
  setEditor,
  wpStatusLoading,
  wpLinked,
  wpSites,
  wpSitesLoading,
  wpSitesError,
  loadWpSites,
}: Props) => (
  <div className="space-y-3">
    {wpStatusLoading ? (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking WordPress.com connection…
      </div>
    ) : wpLinked === false ? (
      <div className="space-y-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 p-3 text-xs">
        <div className="flex items-start gap-2 text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="space-y-1">
            <p className="font-medium">WordPress.com is not linked to this workspace</p>
            <p className="text-muted-foreground">
              A workspace admin needs to link the WordPress.com connector before you can save this
              publishing destination. Open <strong>Workspace settings → Connectors</strong> and connect a
              WordPress.com account, then return here and reopen this dialog.
            </p>
          </div>
        </div>
      </div>
    ) : (
      <>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="connector-wp-site">WordPress.com site</Label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => void loadWpSites()}
              disabled={wpSitesLoading}
            >
              {wpSitesLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {wpSites === null ? "Load my sites" : "Reload sites"}
            </Button>
          </div>
          <Input
            id="connector-wp-site"
            value={editor.wpSite}
            onChange={(e) => setEditor((s) => ({ ...s, wpSite: e.target.value }))}
            placeholder="myblog.wordpress.com"
          />
          {wpSites && wpSites.length > 0 ? (
            <div className="space-y-1">
              <Label htmlFor="connector-wp-site-picker" className="text-xs text-muted-foreground">
                Or pick from your linked account
              </Label>
              <select
                id="connector-wp-site-picker"
                value=""
                onChange={(e) => {
                  const picked = wpSites.find((s) => s.url === e.target.value);
                  if (!picked) return;
                  const host = picked.url.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
                  setEditor((s) => ({ ...s, wpSite: host }));
                }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select a site…</option>
                {wpSites.map((s) => (
                  <option key={s.id} value={s.url}>
                    {s.name} — {s.url.replace(/^https?:\/\//i, "")}
                    {s.is_private ? " (private)" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {wpSitesError ? <p className="text-xs text-destructive">{wpSitesError}</p> : null}
          <p className="text-xs text-muted-foreground">
            Enter the site hostname (e.g. <code>myblog.wordpress.com</code>) or numeric blog ID. Posts are
            created as drafts so you can review and publish from WordPress.com.
          </p>
        </div>
        <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          Authentication is handled by the workspace-level WordPress.com connection — no app password is
          stored on this connector. Make sure the linked WordPress.com account has author access to the
          site above.
        </div>
      </>
    )}
  </div>
);
